// A sample AFS (Advantage Flow System) stats view, embedded in the "Taking the
// CLA a few layers deeper" article so a reader sees what advantage tracking
// would look like inside the platform.
//
// Everything here is MOCK. The roster is the current Cleveland Cavaliers (by
// jersey), and the numbers are invented but hand-tuned to read realistically:
// possessions involved track playing time and on-ball role (the lead guards
// touch the most, bigs and bench less), and no rate sits at a fake 100%.
//
// Scope follows Coleman's call on what's actually trackable: the four headline
// rates, a defensive save rate, leakage, and the player profiles (the
// centerpiece). Beaten-by, chains and action effectiveness were dropped.
//
// BAM branding only: gold, white, black. Theme (`C`, `dark`) comes from the
// reader, so light and dark both work. No external data, no state.

import React from "react";
import { GOLD } from "../constants/theme";

// BAM's targets from the AFS spec, not league benchmarks.
const TARGETS = { acr: 0.5, maint: 0.6, advShot: 0.6, conv: 0.45 };

// The four headline rates (team, offense). Invented, near BAM's targets.
const TILES = [
  { k: "Advantage Creation Rate", f: "created ÷ possessions", v: 0.56, t: TARGETS.acr, a: 124, b: 221 },
  { k: "Maintenance Rate", f: "maintained ÷ created", v: 0.64, t: TARGETS.maint, a: 79, b: 124 },
  { k: "Adv Shot Ratio", f: "advantage shots ÷ all shots", v: 0.59, t: TARGETS.advShot, a: 92, b: 156, key: true },
  { k: "Conversion Rate", f: "makes ÷ all shots", v: 0.46, t: TARGETS.conv, a: 72, b: 156 },
];

// Defensive save rate: opponent advantages stifled before an advantage shot.
const SAVE = { v: 0.61, a: 47, b: 77 };

// Where the 45 lost advantages died.
const LEAK = [["Def. Recovered", 16], ["Bad Pass", 10], ["Stagnation", 8], ["Turnover", 6], ["Shot Clock", 3], ["Off. Foul", 2]];
const LOST_TOTAL = 45;

// Player advantage profiles. poss = possessions the player had a hand in
// (created, maintained, or shot), weighted by minutes and on-ball role: the
// starting guards lead, the bench bigs trail. Already sorted by poss.
//
// c75 = advantages created per 75 offensive possessions on the floor: a RATE,
// so a bench creator (Porter, Proctor) can rank above a starter who touches
// the ball far more but creates less often per chance. role is the player's
// primary job in the advantage chain:
//   Creator   — beats his man to create the advantage
//   Connector — keeps a live advantage moving (passing, short-roll reads)
//   Finisher  — ends it, converts the shot
const PLAYERS = [
  { j: "45", name: "Mitchell", poss: 52, created: 34, c75: 13.2, role: "Creator", mOpp: 20, mOk: 13, shots: 30, made: 14 },
  { j: "1", name: "Harden", poss: 50, created: 31, c75: 11.8, role: "Connector", mOpp: 26, mOk: 18, shots: 18, made: 8 },
  { j: "4", name: "Mobley", poss: 41, created: 18, c75: 6.1, role: "Connector", mOpp: 19, mOk: 12, shots: 22, made: 12 },
  { j: "8", name: "Watson", poss: 30, created: 12, c75: 5.4, role: "Finisher", mOpp: 10, mOk: 6, shots: 16, made: 7 },
  { j: "31", name: "Allen", poss: 27, created: 8, c75: 3.0, role: "Finisher", mOpp: 12, mOk: 8, shots: 15, made: 11 },
  { j: "5", name: "Merrill", poss: 22, created: 6, c75: 2.6, role: "Finisher", mOpp: 8, mOk: 5, shots: 17, made: 7 },
  { j: "9", name: "Porter Jr.", poss: 20, created: 11, c75: 9.3, role: "Creator", mOpp: 7, mOk: 4, shots: 10, made: 4 },
  { j: "33", name: "Hezonja", poss: 18, created: 7, c75: 6.8, role: "Creator", mOpp: 6, mOk: 4, shots: 12, made: 5 },
  { j: "20", name: "Tyson", poss: 15, created: 5, c75: 4.5, role: "Finisher", mOpp: 5, mOk: 3, shots: 9, made: 4 },
  { j: "24", name: "Proctor", poss: 12, created: 6, c75: 8.1, role: "Creator", mOpp: 4, mOk: 2, shots: 7, made: 2 },
  { j: "3", name: "Bryant", poss: 10, created: 2, c75: 2.2, role: "Finisher", mOpp: 4, mOk: 3, shots: 6, made: 4 },
  { j: "88", name: "Udeh Jr.", poss: 6, created: 1, c75: 2.0, role: "Finisher", mOpp: 2, mOk: 1, shots: 3, made: 1 },
];
const MAX_POSS = Math.max(...PLAYERS.map((p) => p.poss));

const pctTxt = (x) => (x == null ? "n/a" : Math.round(x * 100) + "%");

export default function AfsMockDashboard({ C, dark, team = "Cleveland Cavaliers" }) {
  const sunk = dark ? "rgba(255,255,255,0.06)" : "#EEEDE6";
  const line = C.border;
  const ink = dark ? GOLD : "#857C2E"; // gold that stays legible on white
  const label = { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.textDim };

  const Panel = ({ title, def, children, pad = 16 }) => (
    <div style={{ background: C.bgCard, border: `1px solid ${line}`, borderRadius: 14, padding: pad, display: "grid", gap: 10, alignContent: "start", minWidth: 0 }}>
      {title && <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{title}</div>}
      {def && <div style={{ fontSize: 12, color: C.textDim, marginTop: -4, lineHeight: 1.45 }}>{def}</div>}
      {children}
    </div>
  );

  const band = (v, t) => (v >= t ? ["At / above target", ink] : v >= t - 0.1 ? ["Close to target", C.textMid] : ["Below target", C.textDim]);

  // Role tag, kept inside the gold/white/black palette: three tiers told apart
  // by fill weight, not by hue. Creator is a solid gold chip, Connector an
  // outlined one, Finisher a quiet neutral one.
  const roleStyle = (role) => {
    const base = { fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", padding: "2px 7px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-block" };
    if (role === "Creator") return { ...base, background: ink, color: dark ? "#111" : "#fff", border: `1px solid ${ink}` };
    if (role === "Connector") return { ...base, background: "transparent", color: ink, border: `1px solid ${ink}` };
    return { ...base, background: sunk, color: C.textMid, border: `1px solid ${line}` };
  };

  return (
    <div style={{ margin: "26px 0", border: `1px solid ${line}`, borderRadius: 16, overflow: "hidden", background: dark ? "rgba(255,255,255,0.015)" : "#FBFBF8" }}>
      {/* header */}
      <div style={{ padding: "16px 18px", borderBottom: `1px solid ${line}`, display: "grid", gap: 6 }}>
        <div style={{ ...label, color: ink }}>BAM Coaches · Advantage Flow System</div>
        <div style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>AFS Stats · {team}</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: C.textMid, background: dark ? "rgba(226,221,159,0.12)" : "rgba(226,221,159,0.45)", padding: "6px 10px", borderRadius: 8, width: "fit-content" }}>
          <strong style={{ color: C.text }}>Sample data.</strong> Invented numbers, current roster. No real game or result.
        </div>
      </div>

      <div style={{ padding: 18, display: "grid", gap: 14 }}>
        {/* headline tiles */}
        <div className="afs-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10 }}>
          {TILES.map((t) => {
            const [bt, bc] = band(t.v, t.t);
            return (
              <div key={t.k} style={{ background: C.bgCard, border: `1px solid ${t.key ? GOLD : line}`, boxShadow: t.key ? `inset 0 0 0 1px ${GOLD}` : "none", borderRadius: 14, padding: 13, display: "grid", gap: 7, alignContent: "start" }}>
                <div style={{ ...label, fontSize: 10 }}>{t.k}{t.key ? " · the one number" : ""}</div>
                <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 0.95, color: C.text, fontVariantNumeric: "tabular-nums" }}>{pctTxt(t.v)}</div>
                <div style={{ position: "relative", height: 6, borderRadius: 3, background: sunk }}>
                  <span style={{ position: "absolute", inset: "0 auto 0 0", width: `${t.v * 100}%`, borderRadius: 3, background: C.text }} />
                  <span style={{ position: "absolute", top: -3, bottom: -3, left: `${t.t * 100}%`, width: 2, background: GOLD }} />
                </div>
                <div style={{ fontSize: 11, color: C.textMid, fontVariantNumeric: "tabular-nums" }}>{t.a} of {t.b} · target {Math.round(t.t * 100)}%</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: bc }}>{bt}</div>
              </div>
            );
          })}
        </div>

        <div className="afs-grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {/* save rate (defense) */}
          <Panel title="Save rate" def="Defense. Opponent advantages you stifled before they got an advantage shot off, ÷ opponent advantages created.">
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontSize: 40, fontWeight: 800, color: C.text, lineHeight: 0.9, fontVariantNumeric: "tabular-nums" }}>{pctTxt(SAVE.v)}</div>
              <div style={{ fontSize: 12.5, color: C.textMid, fontVariantNumeric: "tabular-nums" }}>{SAVE.a} of {SAVE.b} opponent advantages</div>
            </div>
            <div style={{ position: "relative", height: 8, borderRadius: 4, background: sunk }}>
              <span style={{ position: "absolute", inset: "0 auto 0 0", width: `${SAVE.v * 100}%`, borderRadius: 4, background: GOLD }} />
            </div>
          </Panel>

          {/* leakage */}
          <Panel title="Leakage: where advantages die" def={`Advantages created and not maintained, by what ended them. ${LOST_TOTAL} of 124 lost.`}>
            <div style={{ display: "grid", gap: 8 }}>
              {LEAK.map(([k, v]) => (
                <div key={k} style={{ display: "grid", gridTemplateColumns: "minmax(96px,150px) 1fr 70px", gap: 10, alignItems: "center", fontSize: 12.5 }}>
                  <span style={{ color: C.text }}>{k}</span>
                  <span style={{ height: 9, borderRadius: 5, background: sunk, position: "relative", overflow: "hidden" }}>
                    <span style={{ position: "absolute", inset: "0 auto 0 0", width: `${(v / LOST_TOTAL) * 100}%`, borderRadius: 5, background: GOLD }} />
                  </span>
                  <span style={{ textAlign: "right", color: C.textMid, fontVariantNumeric: "tabular-nums" }}>{v} · {pctTxt(v / LOST_TOTAL)}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* player advantage profiles — the centerpiece */}
        <div style={{ background: C.bgCard, border: `1px solid ${GOLD}`, boxShadow: `inset 0 0 0 1px ${GOLD}`, borderRadius: 16, padding: "18px 18px 16px", display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gap: 4 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.01em" }}>Player advantage profiles</div>
            <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.5 }}>
              Who creates advantages, who keeps them alive, and who finishes them. Ordered by possessions involved, which tracks playing time and on-ball role. Created / 75 is a rate (per 75 offensive possessions), so a bench creator can out-rank a high-usage starter. Each player carries a role tag for his primary job in the chain. Mock data.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 2 }}>
              <span style={roleStyle("Creator")}>Creator</span>
              <span style={roleStyle("Connector")}>Connector</span>
              <span style={roleStyle("Finisher")}>Finisher</span>
            </div>
          </div>
          <div style={{ overflowX: "auto", margin: "0 -2px" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
              <thead>
                <tr>
                  {["Player", "Possessions involved", "Created", "Created / 75", "Maint %", "Shots", "Conv %"].map((h, i) => (
                    <th key={h} style={{ ...label, fontSize: 10, padding: "8px 10px", textAlign: i === 0 || i === 1 ? "left" : "right", borderBottom: `1px solid ${GOLD}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PLAYERS.map((p) => {
                  const mPct = p.mOpp ? p.mOk / p.mOpp : null;
                  const conv = p.shots ? p.made / p.shots : null;
                  return (
                    <tr key={p.j}>
                      <td style={{ padding: "9px 10px", textAlign: "left", borderBottom: `1px solid ${line}`, color: C.text, fontWeight: 800, whiteSpace: "nowrap" }}>
                        <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
                          <span><span style={{ color: ink }}>#{p.j}</span> {p.name}</span>
                          <span style={roleStyle(p.role)}>{p.role}</span>
                        </div>
                      </td>
                      <td style={{ padding: "9px 10px", borderBottom: `1px solid ${line}`, minWidth: 150 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ flex: 1, height: 10, borderRadius: 5, background: sunk, position: "relative", overflow: "hidden", minWidth: 60 }}>
                            <span style={{ position: "absolute", inset: "0 auto 0 0", width: `${(p.poss / MAX_POSS) * 100}%`, borderRadius: 5, background: GOLD }} />
                          </span>
                          <span style={{ color: C.textMid, fontVariantNumeric: "tabular-nums", width: 22, textAlign: "right" }}>{p.poss}</span>
                        </div>
                      </td>
                      <td style={numCell(C, line)}>{p.created}</td>
                      <td style={numCell(C, line)}>{p.c75.toFixed(1)}</td>
                      <td style={numCell(C, line, p.mOpp < 6)}>{pctTxt(mPct)}</td>
                      <td style={numCell(C, line)}>{p.shots}</td>
                      <td style={numCell(C, line, p.shots < 6)}>{pctTxt(conv)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: C.textDim }}>Faint cells are small samples. Targets shown above are BAM's (50 / 60 / 60 / 45%), not league benchmarks.</div>
        </div>
      </div>

      <style>{`
        @media (max-width: 620px) {
          .afs-tiles { grid-template-columns: repeat(2,minmax(0,1fr)) !important; }
          .afs-grid2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function numCell(C, line, faint) {
  return { padding: "9px 10px", textAlign: "right", borderBottom: `1px solid ${line}`, color: faint ? C.textDim : C.textMid, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
}
